import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, MapPin, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const AddParkingDialog = ({ open, onOpenChange, vehicles, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [places, setPlaces] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    vehicle_id: '',
    is_primary: false,
    
    // Location
    address: '',
    place_name: '',
    where_i_parked: '',
    latitude: null,
    longitude: null,
    
    // Structured
    garage_name: '',
    level_floor: '',
    section_zone: '',
    spot_number: '',
    row_aisle: '',
    landmarks: '',
    shuttle_info: '',
    notes: '',
    
    // Payment
    paid_until: '',
    leave_by: '',
    hourly_rate: '',
    amount_paid: '',
    
    // Saved place
    place_id: '',
  });

  useEffect(() => {
    if (open) {
      fetchPlaces();
    }
  }, [open]);

  const fetchPlaces = async () => {
    try {
      const response = await API.get('/parking/places/all');
      setPlaces(response.data);
    } catch (error) {
      console.error('Failed to fetch places:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePlaceSelect = (placeId) => {
    if (placeId === 'none') {
      setFormData(prev => ({ 
        ...prev, 
        place_id: '',
        address: '',
        place_name: ''
      }));
      return;
    }
    
    const place = places.find(p => p.id === placeId);
    if (place) {
      setFormData(prev => ({
        ...prev,
        place_id: place.id,
        address: place.address || '',
        place_name: place.name,
        notes: place.default_notes || prev.notes,
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      vehicle_id: '',
      is_primary: false,
      address: '',
      place_name: '',
      where_i_parked: '',
      garage_name: '',
      level_floor: '',
      section_zone: '',
      spot_number: '',
      row_aisle: '',
      landmarks: '',
      shuttle_info: '',
      notes: '',
      paid_until: '',
      leave_by: '',
      hourly_rate: '',
      amount_paid: '',
      place_id: '',
    });
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }

    setLocationLoading(true);
    
    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          // Reverse geocode to get address
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            {
              headers: {
                'Accept': 'application/json',
              }
            }
          );
          
          if (!response.ok) {
            throw new Error('Geocoding failed');
          }
          
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            address: data.display_name || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            latitude,
            longitude,
          }));
          toast.success('Location captured!');
        } catch (error) {
          console.error('Geocoding error:', error);
          setFormData(prev => ({
            ...prev,
            address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            latitude,
            longitude,
          }));
          toast.success('Location coordinates captured!');
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        setLocationLoading(false);
        let errorMessage = 'Could not get your location.';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please try again.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'Could not get your location. Please enter manually.';
        }
        
        toast.error(errorMessage);
      },
      options
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }
    
    setLoading(true);
    try {
      const dataToSend = {
        title: formData.title,
        vehicle_id: formData.vehicle_id || null,
        is_primary: formData.is_primary,
        
        address: formData.address || null,
        place_name: formData.place_name || null,
        where_i_parked: formData.where_i_parked || null,
        latitude: formData.latitude || null,
        longitude: formData.longitude || null,
        
        garage_name: formData.garage_name || null,
        level_floor: formData.level_floor || null,
        section_zone: formData.section_zone || null,
        spot_number: formData.spot_number || null,
        row_aisle: formData.row_aisle || null,
        landmarks: formData.landmarks || null,
        shuttle_info: formData.shuttle_info || null,
        notes: formData.notes || null,
        
        paid_until: formData.paid_until ? new Date(formData.paid_until).toISOString() : null,
        leave_by: formData.leave_by ? new Date(formData.leave_by).toISOString() : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        amount_paid: formData.amount_paid ? parseFloat(formData.amount_paid) : null,
        
        place_id: formData.place_id || null,
      };
      
      await API.post('/parking', dataToSend);
      toast.success('Parking saved!');
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Failed to save parking:', error);
      toast.error('Failed to save parking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Where Did You Park?</DialogTitle>
          <DialogDescription>
            Save your parking location so you can find your car later
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Quick select from saved places */}
          {places.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="place_id">Quick Select Saved Place</Label>
              <Select value={formData.place_id || 'none'} onValueChange={handlePlaceSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a saved place..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Enter manually --</SelectItem>
                  {places.map(place => (
                    <SelectItem key={place.id} value={place.id}>
                      {place.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Mall Parking, Airport Lot C"
                data-testid="input-parking-title"
              />
            </div>
            
            {vehicles && vehicles.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="vehicle_id">Vehicle</Label>
                <Select value={formData.vehicle_id || 'none'} onValueChange={(v) => handleChange('vehicle_id', v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No vehicle --</SelectItem>
                    {vehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.year} {v.make} {v.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center space-x-2 pt-6">
              <Switch
                id="is_primary"
                checked={formData.is_primary}
                onCheckedChange={(checked) => handleChange('is_primary', checked)}
              />
              <Label htmlFor="is_primary">Primary vehicle</Label>
            </div>
          </div>
          
          {/* Location */}
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Location</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGetLocation}
                disabled={locationLoading}
                data-testid="use-current-location-btn"
              >
                {locationLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Getting Location...
                  </>
                ) : (
                  <>
                    <Navigation className="h-4 w-4 mr-1" />
                    Use Current Location
                  </>
                )}
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Street address or location"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="where_i_parked">Where Exactly?</Label>
              <Textarea
                id="where_i_parked"
                value={formData.where_i_parked}
                onChange={(e) => handleChange('where_i_parked', e.target.value)}
                placeholder="Describe where you parked... e.g., Near entrance, by the blue pillar"
                rows={2}
              />
            </div>
          </div>
          
          {/* Structured parking info */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Parking Structure Details (optional)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="garage_name">Garage / Lot Name</Label>
                <Input
                  id="garage_name"
                  value={formData.garage_name}
                  onChange={(e) => handleChange('garage_name', e.target.value)}
                  placeholder="e.g., Garage A, North Lot"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="level_floor">Level / Floor</Label>
                <Input
                  id="level_floor"
                  value={formData.level_floor}
                  onChange={(e) => handleChange('level_floor', e.target.value)}
                  placeholder="e.g., P2, Level 3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="section_zone">Section / Zone</Label>
                <Input
                  id="section_zone"
                  value={formData.section_zone}
                  onChange={(e) => handleChange('section_zone', e.target.value)}
                  placeholder="e.g., Section B, Blue Zone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="spot_number">Spot Number</Label>
                <Input
                  id="spot_number"
                  value={formData.spot_number}
                  onChange={(e) => handleChange('spot_number', e.target.value)}
                  placeholder="e.g., 42, A15"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="row_aisle">Row / Aisle</Label>
                <Input
                  id="row_aisle"
                  value={formData.row_aisle}
                  onChange={(e) => handleChange('row_aisle', e.target.value)}
                  placeholder="e.g., Row C, Aisle 5"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="landmarks">Landmarks</Label>
                <Input
                  id="landmarks"
                  value={formData.landmarks}
                  onChange={(e) => handleChange('landmarks', e.target.value)}
                  placeholder="e.g., Near elevator, red pillar"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="shuttle_info">Shuttle Info</Label>
              <Input
                id="shuttle_info"
                value={formData.shuttle_info}
                onChange={(e) => handleChange('shuttle_info', e.target.value)}
                placeholder="e.g., Blue shuttle, every 10 mins"
              />
            </div>
          </div>
          
          {/* Payment info */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Payment & Timing (optional)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="amount_paid">Amount Paid ($)</Label>
                <Input
                  id="amount_paid"
                  type="number"
                  step="0.01"
                  value={formData.amount_paid}
                  onChange={(e) => handleChange('amount_paid', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  value={formData.hourly_rate}
                  onChange={(e) => handleChange('hourly_rate', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paid_until">Meter Paid Until</Label>
                <Input
                  id="paid_until"
                  type="datetime-local"
                  value={formData.paid_until}
                  onChange={(e) => handleChange('paid_until', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="leave_by">Leave By</Label>
                <Input
                  id="leave_by"
                  type="datetime-local"
                  value={formData.leave_by}
                  onChange={(e) => handleChange('leave_by', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          {/* Notes */}
          <div className="border-t pt-4 space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any other details..."
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#D4C5E2] hover:bg-[#C4B5D2] text-gray-900"
              data-testid="submit-parking-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Parking
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddParkingDialog;
